import * as React from 'react';
import { type StageErrorSummary } from '@chess/api-insights';
import { CountBars, SkeletonRows, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { LOST_PIECES, type LostPiece } from '@chess/api-model';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';

// The ending blunders by the piece each gave away, read from the engine's
// reply, as the middlegame's card does: one bar per piece and "Allowed
// mate"; each bar opens those games. Blunders that cost position rather
// than material have no bar. Drawn from props only.

export type EndingPiecesCardProps = CardProps<{ endgame: StageErrorSummary }, {
  /** Opens the library filtered to the games with that piece given away. */
  link?: FilterLink;
}>;

export const TITLE = 'Blunders by piece lost';
const ASIDE = 'ending';
const LABEL: Record<LostPiece, string> = { pawn: 'Pawn', knight: 'Knight', bishop: 'Bishop', rook: 'Rook', queen: 'Queen', mate: 'Allowed mate' };
const WHAT: Record<LostPiece, string> = { pawn: 'gave away a pawn', knight: 'gave away a knight', bishop: 'gave away a bishop', rook: 'gave away a rook', queen: 'gave away the queen', mate: 'allowed a forced mate' };

export const EndingPiecesCard: React.FC<EndingPiecesCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardNote>
          <TextSkeleton sample="3 of 4 ending blunders cost material" />
        </CardNote>
        <SkeletonRows labels={LOST_PIECES.map((p) => LABEL[p])} value="0" />
      </>
    );
  const { endgame, link } = props;
  const current = endgame;
  const material = LOST_PIECES.reduce((n, p) => n + current.lost[p], 0);
  const rows = LOST_PIECES.map((p) => ({
    key: p,
    label: LABEL[p],
    count: current.lost[p],
    title: `${current.lost[p]} ${current.lost[p] === 1 ? 'blunder' : 'blunders'} ${WHAT[p]}, in ${current.lostGames[p]} ${current.lostGames[p] === 1 ? 'game' : 'games'} · opens those games`,
    filter: { blunder: `ending-${p}` as const },
  }));
  const note = current.blunders ? `${material} of ${current.blunders} ending ${current.blunders === 1 ? 'blunder' : 'blunders'} cost material` : undefined;
  let body: React.ReactNode;
  if (!current.scanned) body = <CardEmpty>Scan games to see what your ending blunders give away.</CardEmpty>;
  else if (!material) body = <CardEmpty>{current.blunders ? 'Your ending blunders cost position, not material.' : 'No ending blunders in the last 30 days.'}</CardEmpty>;
  else body = <CountBars rows={rows} link={link} />;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {note && <CardNote>{note}</CardNote>}
      {body}
    </>
  );
};
